/**
 * Which scroll buttons to offer, and how far one press moves.
 *
 * The screen in the kitchen does not scroll by touch. These buttons are not a
 * convenience on top of scrolling — they are the scrolling. A rule that hides
 * the down button one pixel early leaves the last dish of a long order
 * unreachable, so the tolerance below is load-bearing.
 */

/**
 * Browsers report fractional heights, and a leftover of a few pixels is not
 * content — it is rounding. Treating it as content leaves a button on screen
 * that scrolls nothing, which teaches the cook the buttons lie.
 */
export const SCROLL_EDGE_TOLERANCE_PX = 8;

/**
 * One press moves nearly a screenful, not all the way to the end. The sliver
 * left behind is what lets someone reading a long order keep their place.
 */
export const SCROLL_PAGE_RATIO = 0.9;

export interface ScrollMetrics {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

export interface ScrollAffordance {
  /** There is content above the fold. */
  canUp: boolean;
  /** There is content below it. */
  canDown: boolean;
}

/** A DOM measurement we can reason about, or zero. */
function finite(value: number): number {
  return Number.isFinite(value) ? value : 0;
}

/**
 * An arrow is offered only when there is content in that direction (FR-009).
 *
 * This replaces two divergent implementations: one showed both arrows whenever
 * anything overflowed, which meant a permanent "up" button at the top of every
 * long comanda.
 */
export function computeScrollAffordance(metrics: ScrollMetrics): ScrollAffordance {
  const scrollTop = Math.max(0, finite(metrics.scrollTop));
  const scrollHeight = finite(metrics.scrollHeight);
  const clientHeight = finite(metrics.clientHeight);

  const overflows = scrollHeight > clientHeight + SCROLL_EDGE_TOLERANCE_PX;
  if (!overflows) return { canUp: false, canDown: false };

  const remainingBelow = scrollHeight - scrollTop - clientHeight;

  return {
    canUp: scrollTop > SCROLL_EDGE_TOLERANCE_PX,
    canDown: remainingBelow > SCROLL_EDGE_TOLERANCE_PX,
  };
}

/**
 * How far one press moves, signed for the direction. Never a jump to the end:
 * a cook reading a twelve-dish order needs the next screenful, not the last.
 */
export function pageScrollDelta(clientHeight: number, direction: "up" | "down"): number {
  const height = Math.max(0, finite(clientHeight));
  if (height === 0) return 0;

  const step = Math.max(1, Math.round(height * SCROLL_PAGE_RATIO));
  return direction === "down" ? step : -step;
}
